import { useMemo, useState } from 'react'
import type { AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'

interface BmiCalculatorTemplateProps {
  stage: WorkflowStage
  initialValue?: { height_feet?: number; height_inches?: number; weight_lbs?: number }
  onSelect: (value: AnswerValue) => void
  currentPhase?: PhaseInfo | null
}

const btnPrimary =
  'w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 hover:not-disabled:shadow-[0_8px_24px_rgba(0,0,0,0.15)] active:not-disabled:translate-y-0 disabled:opacity-25 disabled:cursor-default'

function calculateBmi(heightInches: number, weightLbs: number): string | null {
  if (!heightInches || !weightLbs) return null
  return ((weightLbs * 703) / (heightInches * heightInches)).toFixed(1)
}

export default function BmiCalculatorTemplate({
  initialValue,
  onSelect,
}: BmiCalculatorTemplateProps) {
  const [feet, setFeet] = useState(initialValue?.height_feet ?? 5)
  const [inches, setInches] = useState(initialValue?.height_inches ?? 6)
  const [weight, setWeight] = useState(initialValue?.weight_lbs ?? 180)
  const [feetInput, setFeetInput] = useState(String(initialValue?.height_feet ?? 5))
  const [inchesInput, setInchesInput] = useState(String(initialValue?.height_inches ?? 6))
  const [weightInput, setWeightInput] = useState(String(initialValue?.weight_lbs ?? 180))

  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

  const heightTotal = feet * 12 + inches
  const bmi = useMemo(() => calculateBmi(heightTotal, weight), [heightTotal, weight])

  const handleContinue = () => {
    onSelect({
      height_feet: feet,
      height_inches: inches,
      weight_lbs: weight,
      calculated_bmi: bmi ? Math.round(parseFloat(bmi) * 10) / 10 : undefined,
    })
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      <fieldset className="border-none flex flex-col gap-2">
        <legend className="text-[13px] font-medium text-[var(--ww-text-2)] mb-1.5">Height</legend>
        <div className="grid grid-cols-2 gap-2.5">
          <div className="stepper-wrap">
            <button className="stepper-btn" onClick={() => { const v = clamp(feet - 1, 3, 7); setFeet(v); setFeetInput(String(v)) }}>−</button>
            <input
              type="number"
              inputMode="numeric"
              min={3}
              max={7}
              value={feetInput}
              onChange={(e) => { setFeetInput(e.target.value); const n = Number(e.target.value); if (!isNaN(n) && n > 0) setFeet(n) }}
              onBlur={() => { const c = clamp(feet, 3, 7); setFeet(c); setFeetInput(String(c)) }}
              aria-label="Height in feet"
            />
            <span className="stepper-suffix">ft</span>
            <button className="stepper-btn" onClick={() => { const v = clamp(feet + 1, 3, 7); setFeet(v); setFeetInput(String(v)) }}>+</button>
          </div>
          <div className="stepper-wrap">
            <button className="stepper-btn" onClick={() => { const v = clamp(inches - 1, 0, 11); setInches(v); setInchesInput(String(v)) }}>−</button>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={11}
              value={inchesInput}
              onChange={(e) => { setInchesInput(e.target.value); const n = Number(e.target.value); if (!isNaN(n) && n >= 0) setInches(n) }}
              onBlur={() => { const c = clamp(inches, 0, 11); setInches(c); setInchesInput(String(c)) }}
              aria-label="Height in inches"
            />
            <span className="stepper-suffix">in</span>
            <button className="stepper-btn" onClick={() => { const v = clamp(inches + 1, 0, 11); setInches(v); setInchesInput(String(v)) }}>+</button>
          </div>
        </div>
      </fieldset>

      <fieldset className="border-none flex flex-col gap-2">
        <legend className="text-[13px] font-medium text-[var(--ww-text-2)] mb-1.5">Weight (lbs)</legend>
        <div className="stepper-wrap">
          <button className="stepper-btn" onClick={() => { const v = clamp(weight - 1, 80, 600); setWeight(v); setWeightInput(String(v)) }}>−</button>
          <input
            type="number"
            inputMode="numeric"
            min={80}
            max={600}
            value={weightInput}
            onChange={(e) => { setWeightInput(e.target.value); const n = Number(e.target.value); if (!isNaN(n) && n > 0) setWeight(n) }}
            onBlur={() => { const c = clamp(weight, 80, 600); setWeight(c); setWeightInput(String(c)) }}
            placeholder="Enter weight"
            aria-label="Weight in pounds"
          />
          <span className="stepper-suffix">lbs</span>
          <button className="stepper-btn" onClick={() => { const v = clamp(weight + 1, 80, 600); setWeight(v); setWeightInput(String(v)) }}>+</button>
        </div>
      </fieldset>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button className={btnPrimary} onClick={handleContinue}>
          Continue
        </button>
      </div>
    </div>
  )
}
